import React, { useState, useEffect } from 'react';
import './WorkstationTab.css';
import './BorrifacaoTab.css';
import ExamModal from './ExamModal';
import { supabase } from '../supabaseClient';
import { pdf } from '@react-pdf/renderer';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { FormularioExamePDFDocument } from './FormularioExamePDF';

/* ─────────────────────────────────────────────
   ExamesTab — Lançamento e consulta de exames
───────────────────────────────────────────── */

const ExamesTab = ({
    registros,
    onReload,
    serverPage = 1,
    serverTotal = 0,
    serverFilters = {},
    allAgentsList = [],
    municipioNome = '',
    onPageChange,
    onFilterChange,
    onFetchAll,
}) => {
    const [localFilters, setLocalFilters] = useState({
        etiqueta: '',
        local: '',
        agente: '',
        atividade: '',
        data_atividade: '',
        data_exame: '',
        status: 'todos',
    });

    const [modoSelecao, setModoSelecao] = useState(false);
    const [selecionados, setSelecionados] = useState(new Set());
    const [modoTodos, setModoTodos]       = useState(false);
    const [gerando, setGerando]           = useState(false);
    const [progresso, setProgresso]       = useState({ total: 0, atual: 0 });

    /* ── Carrega exames vinculados aos registros exibidos ── */
    const [fullExamsData, setFullExamsData] = useState([]);

    useEffect(() => {
        const fetchExams = async () => {
            if (!registros || registros.length === 0) { setFullExamsData([]); return; }
            const ids = registros.map(r => r.id);
            let query = supabase
                .from('exames')
                .select('*, exames_exemplares(*)')
                .in('ocorrencia_id', ids);
            if (localFilters.agente) {
                query = query.ilike('tecnico_responsavel', `%${localFilters.agente}%`);
            }
            const { data, error } = await query;
            if (!error) setFullExamsData(data || []);
        };
        fetchExams();
    }, [registros, localFilters.agente]);

    /* ── Sincroniza filtros com o servidor ── */
    useEffect(() => {
        if (serverFilters) {
            // Preserva o filtro local de agente (não enviado ao servidor)
            setLocalFilters(prev => ({ ...serverFilters, agente: prev.agente }));
        }
    }, [serverFilters]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (onFilterChange) {
                const { agente, ...filtersParaBackend } = localFilters;
                const { agente: _sa, ...serverFiltersWithoutAgente } = serverFilters;
                const mudou = JSON.stringify(filtersParaBackend) !== JSON.stringify(serverFiltersWithoutAgente);
                if (mudou) onFilterChange(filtersParaBackend);
            }
        }, 600);
        return () => clearTimeout(timer);
    }, [localFilters, onFilterChange, serverFilters]);

    /* ── Seleção ── */
    const toggleModoSelecao = () => {
        setModoSelecao(p => !p);
        setSelecionados(new Set());
        setModoTodos(false);
        setProgresso({ total: 0, atual: 0 });
    };

    const toggleSelecionado = (id) => {
        setModoTodos(false);
        setSelecionados(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleTodos = () => {
        setModoTodos(false);
        if (selecionados.size === registros.length) {
            setSelecionados(new Set());
        } else {
            setSelecionados(new Set(registros.map(r => r.id)));
        }
    };

    const todaPaginaSelecionada = registros.length > 0 && selecionados.size === registros.length;

    const sanitize = (str) =>
        String(str || '').trim()
            .normalize('NFD').replace(/[̀-ͯ]/g, '')
            .replace(/[^a-zA-Z0-9\s]/g, '')
            .replace(/\s+/g, '_')
            .slice(0, 25);

    const gerarPDFsEBaixar = async (lista) => {
        if (!lista || lista.length === 0) return;
        
        setProgresso({ total: lista.length, atual: 0 });
        const zip = new JSZip();

        // Buscar todos os exames dos registros selecionados
        const ids = lista.map(r => r.id);
        const { data: examesData } = await supabase
            .from('exames')
            .select('*, exames_exemplares(*)')
            .in('ocorrencia_id', ids);

        const examesMap = {};
        if (examesData) {
            examesData.forEach(e => {
                examesMap[e.ocorrencia_id] = {
                    ...e,
                    exemplares: (e.exames_exemplares || []).map(ex => ({
                        ...ex,
                        vetor: ex.vetor?.replace(/^\d+\s-\s/, '') || '',
                        captura: ex.captura?.replace(/^\d+\s-\s/, '') || '',
                        estagio: ex.estagio?.replace(/^\d+\s-\s/, '') || '',
                        resultado: ex.resultado?.replace(/^\d+\s-\s/, '') || '',
                    }))
                };
            });
        }

        for (let i = 0; i < lista.length; i++) {
            const row = lista[i];
            setProgresso(p => ({ ...p, atual: i + 1 }));
            
            const examData = examesMap[row.id];
            if (!examData) continue; // Pula se não tiver exame

            try {
                const parts = [
                    sanitize(row.codigo_etiqueta),
                    sanitize(row.endereco),
                    sanitize(row.localidade),
                ].filter(Boolean);
                const nomeArquivo = `${i+1}_EXAME_${parts.join('_')}.pdf`.replace(/_+/g, '_');

                const doc = (
                    <FormularioExamePDFDocument 
                        examData={examData} 
                        fieldData={row} 
                        municipioNome={municipioNome} 
                    />
                );

                const blob = await pdf(doc).toBlob();
                if (blob) {
                    zip.file(nomeArquivo, blob);
                }

                if (i % 5 === 0) await new Promise(r => setTimeout(r, 10));
            } catch (errRow) {
                console.error(`Erro no exame do registro ${row.id}:`, errRow);
            }
        }

        try {
            const zipBlob = await zip.generateAsync({ type: 'blob' });
            const dataHoje = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
            saveAs(zipBlob, `laudos-exame-${dataHoje}.zip`);
        } catch (errZip) {
            console.error("Erro ZIP final:", errZip);
            alert("Erro ao finalizar o arquivo ZIP.");
        } finally {
            setProgresso({ total: 0, atual: 0 });
        }
    };

    const handleBaixarZip = async () => {
        setGerando(true);
        try {
            let lista = [];
            if (modoTodos && onFetchAll) {
                lista = await onFetchAll();
            } else {
                lista = registros.filter(r => selecionados.has(r.id));
            }

            // Filtrar apenas registros que possuem exame
            const idsComExame = fullExamsData.map(e => e.ocorrencia_id);
            const listaComExame = lista.filter(r => idsComExame.includes(r.id) || (modoTodos && r.exame_id));

            if (!listaComExame || listaComExame.length === 0) {
                alert("Nenhum registro com exame selecionado.");
                setGerando(false);
                return;
            }

            await gerarPDFsEBaixar(listaComExame);
        } catch (err) {
            console.error('Erro ao gerar ZIP:', err);
            alert('Erro ao gerar os PDFs. Tente novamente.');
        } finally {
            setGerando(false);
        }
    };

    /* ── Modal de exame ── */
    const [examModalState, setExamModalState] = useState({
        isOpen: false,
        etiquetaInicial: '',
        ocorrenciaId: null,
        mode: 'create',
        examData: null,
    });

    const handleSaveExam = async (examData) => {
        if (!examData) { alert('Erro: Dados do exame não encontrados.'); return; }
        if (!examData.data_exame) { alert('A Data do Exame é obrigatória.'); return; }
        try {
            if (examData.id) {
                const { data: updated, error: errUp } = await supabase
                    .from('exames')
                    .update({
                        tecnico_responsavel: examData.tecnico_responsavel,
                        data_exame: examData.data_exame,
                        situacao: examData.situacao,
                    })
                    .eq('id', examData.id)
                    .select()
                    .single();
                if (errUp) throw errUp;

                await supabase.from('exames_exemplares').delete().eq('exame_id', updated.id);
                if (examData.exemplares?.length > 0) {
                    const { error: errEx } = await supabase.from('exames_exemplares').insert(
                        examData.exemplares.map(ex => ({
                            exame_id: updated.id,
                            vetor: ex.vetor,
                            captura: ex.captura,
                            estagio: ex.estagio,
                            resultado: ex.resultado,
                            data_laudo: ex.dataLaudo || null,
                        }))
                    );
                    if (errEx) throw errEx;
                }
                alert('Exame atualizado com sucesso!');
            } else {
                if (!examData.ocorrencia_id) { alert('Erro: ID da ocorrência não encontrado.'); return; }
                const { data: novo, error: errNew } = await supabase
                    .from('exames')
                    .insert({
                        ocorrencia_id: examData.ocorrencia_id,
                        tecnico_responsavel: examData.tecnico_responsavel,
                        data_exame: examData.data_exame,
                        situacao: examData.situacao,
                    })
                    .select()
                    .single();
                if (errNew) throw errNew;

                if (examData.exemplares?.length > 0) {
                    const { error: errEx } = await supabase.from('exames_exemplares').insert(
                        examData.exemplares.map(ex => ({
                            exame_id: novo.id,
                            vetor: ex.vetor,
                            captura: ex.captura,
                            estagio: ex.estagio,
                            resultado: ex.resultado,
                            data_laudo: ex.dataLaudo || null,
                        }))
                    );
                    if (errEx) throw errEx;
                }
                alert('Exame salvo com sucesso!');
            }
            setExamModalState({ isOpen: false });
            if (onReload) onReload();
        } catch (err) {
            console.error('Erro ao salvar exame:', err);
            alert(`Falha ao salvar o exame: ${err.message}`);
        }
    };

    const openExamModal = (row, mode) => {
        let examDataForModal = mode === 'create' ? { exemplares: [] } : row.exame_vinculado;
        if (mode === 'edit' && row.exame_vinculado) {
            examDataForModal = {
                ...row.exame_vinculado,
                exemplares: (row.exame_vinculado.exames_exemplares || []).map(ex => ({
                    ...ex,
                    dataLaudo: ex.data_laudo,
                })),
            };
        }
        setExamModalState({
            isOpen: true,
            etiquetaInicial: row.codigo_etiqueta,
            ocorrenciaId: row.id,
            mode,
            examData: examDataForModal,
        });
    };

    const handleDeleteExam = async (exameId) => {
        if (!window.confirm('Tem certeza que deseja excluir este exame? Esta ação não pode ser desfeita.')) return;
        try {
            await supabase.from('exames_exemplares').delete().eq('exame_id', exameId);
            const { error } = await supabase.from('exames').delete().eq('id', exameId);
            if (error) throw error;
            if (onReload) onReload();
        } catch (err) {
            alert('Erro ao excluir o exame: ' + err.message);
        }
    };

    const handleClearFilters = () => {
        const empty = { etiqueta: '', local: '', agente: '', atividade: '', data_atividade: '', data_exame: '', status: 'todos' };
        setLocalFilters(empty);
        if (onFilterChange) onFilterChange(empty);
    };

    const hasFilters = localFilters.etiqueta || localFilters.local || localFilters.agente ||
        localFilters.data_exame || localFilters.status !== 'todos';

    const itemsPerPage = 50;
    const totalPages = Math.ceil(serverTotal / itemsPerPage);
    const startRecord = (serverPage - 1) * itemsPerPage + 1;
    const endRecord = Math.min(serverPage * itemsPerPage, serverTotal);

    const dataDisplay = registros.map(reg => ({
        ...reg,
        exame_vinculado: fullExamsData.find(e => e.ocorrencia_id === reg.id),
    }));

    const qtdSelecionada = modoTodos ? serverTotal : selecionados.size;

    return (
        <div className="workstation-container">
            <div className="workspace-panel">

                {/* ── Filtros ── */}
                <div className="toolbar-container">
                    <div className="filter-grid">
                        <div className="filter-item">
                            <label>Etiqueta</label>
                            <input
                                type="text"
                                placeholder="Etiqueta..."
                                value={localFilters.etiqueta}
                                onChange={e => setLocalFilters(p => ({ ...p, etiqueta: e.target.value }))}
                                className="filter-input input-small"
                            />
                        </div>
                        <div className="filter-item">
                            <label>Localidade</label>
                            <input
                                type="text"
                                placeholder="Localidade / Endereço..."
                                value={localFilters.local}
                                onChange={e => setLocalFilters(p => ({ ...p, local: e.target.value }))}
                                className="filter-input input-medium"
                            />
                        </div>
                        <div className="filter-item">
                            <label>Técnico Responsável</label>
                            <input
                                type="text"
                                placeholder="Nome do técnico..."
                                value={localFilters.agente}
                                onChange={e => setLocalFilters(p => ({ ...p, agente: e.target.value }))}
                                className="filter-input input-medium"
                            />
                        </div>
                        <div className="filter-item">
                            <label htmlFor="ex_data_ex">Data do Exame</label>
                            <input
                                id="ex_data_ex"
                                type="date"
                                value={localFilters.data_exame}
                                onChange={e => setLocalFilters(p => ({ ...p, data_exame: e.target.value }))}
                                className="filter-input"
                            />
                        </div>
                        <div className="filter-item">
                            <label>Status</label>
                            <select
                                value={localFilters.status}
                                onChange={e => setLocalFilters(p => ({ ...p, status: e.target.value }))}
                                className="filter-select"
                            >
                                <option value="todos">Todos</option>
                                <option value="pendente_exame">Pendente de Exame</option>
                                <option value="positivo">Positivo</option>
                                <option value="negativo">Negativo</option>
                            </select>
                        </div>
                        {hasFilters && (
                            <button className="btn-clear" onClick={handleClearFilters}>Limpar</button>
                        )}
                    </div>
                    
                    <button
                        onClick={toggleModoSelecao}
                        className="btn-download-trigger"
                        style={{ background: modoSelecao ? '#1d4ed8' : '#2563eb' }}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="7 10 12 15 17 10"/>
                            <line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                        {modoSelecao ? 'Cancelar seleção' : 'Baixar Laudos'}
                    </button>
                </div>

                {/* ── Barra de seleção ── */}
                {modoSelecao && (
                    <div className="selection-info-bar">
                        {qtdSelecionada === 0 ? (
                            <span className="selection-msg">Selecione os exames que deseja baixar</span>
                        ) : (
                            <>
                                <span className="selection-status-text">
                                    {modoTodos
                                        ? `Todos os ${serverTotal} registros selecionados`
                                        : `${selecionados.size} registro${selecionados.size > 1 ? 's' : ''} desta página selecionado${selecionados.size > 1 ? 's' : ''}`}
                                </span>

                                {todaPaginaSelecionada && !modoTodos && serverTotal > registros.length && (
                                    <button onClick={() => setModoTodos(true)} className="btn-select-all-server">
                                        Selecionar todos os {serverTotal} registros
                                    </button>
                                )}

                                {modoTodos && (
                                    <button onClick={() => { setModoTodos(false); setSelecionados(new Set(registros.map(r => r.id))); }} className="btn-select-only-page">
                                        Selecionar apenas esta página
                                    </button>
                                )}

                                <button
                                    onClick={handleBaixarZip}
                                    disabled={gerando}
                                    className="btn-execute-download"
                                    style={{ background: gerando ? '#6b7280' : '#2563eb', cursor: gerando ? 'wait' : 'pointer' }}
                                >
                                    {gerando 
                                        ? (progresso.total > 0 ? `Gerando (${progresso.atual}/${progresso.total})...` : 'Iniciando...') 
                                        : `Baixar ${qtdSelecionada} Laudo${qtdSelecionada > 1 ? 's' : ''}`}
                                </button>
                            </>
                        )}
                    </div>
                )}

                <div className="pagination-info-top">
                    Exibindo registros <strong>{serverTotal > 0 ? startRecord : 0} – {endRecord}</strong> de <strong>{serverTotal}</strong>
                </div>

                {/* ── Tabela ── */}
                <div className="table-wrapper">
                    <table className="technical-grid">
                        <thead>
                            <tr>
                                {modoSelecao && (
                                    <th className="th-center" style={{ width: 36 }}>
                                        <input
                                            type="checkbox"
                                            checked={selecionados.size === registros.length && registros.length > 0}
                                            onChange={toggleTodos}
                                            title="Selecionar todos"
                                        />
                                    </th>
                                )}
                                <th className="th-center" style={{ width: 90 }}>Etiqueta</th>
                                <th className="th-center" style={{ width: 110 }}>Data Form. Campo</th>
                                <th className="th-left">Localidade / Endereço</th>
                                <th className="th-left" style={{ minWidth: 100 }}>Técnico Responsável</th>
                                <th className="th-center" style={{ minWidth: 110 }}>Captura</th>
                                <th className="th-center border-left-soft" style={{ width: 110 }}>Data Exame</th>
                                <th className="th-center" style={{ minWidth: 120 }}>Laudo</th>
                                <th className="th-center">Resultado</th>
                                <th className="th-center" style={{ width: 110 }}>Ação</th>
                            </tr>
                        </thead>
                        <tbody>
                            {dataDisplay.map(row => {
                                const temEtiqueta = !!row.codigo_etiqueta;
                                const temExame = !!row.exame_vinculado;
                                const exemplares = temExame ? (row.exame_vinculado.exames_exemplares || []) : [];
                                const algumPositivo = exemplares.some(ex => ex.resultado?.includes('Positivo'));
                                const exemplarPrincipal = exemplares.find(ex => ex.resultado?.includes('Positivo')) || exemplares[0];
                                const nomeVetor = temExame && exemplarPrincipal
                                    ? (exemplarPrincipal.vetor?.split(' - ')[1] || exemplarPrincipal.vetor)
                                    : '-';
                                const capturaCount = (row.barbeiros_intradomicilio || 0) + (row.barbeiros_peridomicilio || 0);

                                return (
                                    <tr key={row.id} style={modoSelecao && selecionados.has(row.id) ? { background: '#f0f9ff' } : {}}>
                                        {modoSelecao && (
                                            <td className="td-center" onClick={() => toggleSelecionado(row.id)} style={{ cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selecionados.has(row.id)}
                                                    onChange={() => toggleSelecionado(row.id)}
                                                    onClick={e => e.stopPropagation()}
                                                />
                                            </td>
                                        )}
                                        <td className="td-center code-font">{row.codigo_etiqueta || <span className="text-muted">-</span>}</td>
                                        <td className="td-center">{new Date(row.data_atividade.replace(/-/g, '/')).toLocaleDateString()}</td>
                                        <td className="td-left">
                                            <div className="fw-bold text-dark">{row.localidade || 'Local n/ inf.'}</div>
                                            <div className="text-small text-muted">{row.endereco}</div>
                                        </td>
                                        <td className="td-left text-small text-dark">{row.exame_vinculado?.tecnico_responsavel || <span className="text-muted">-</span>}</td>
                                        <td className="td-center">
                                            {capturaCount > 0 ? (
                                                <span className="badge-capture" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>Captura {capturaCount}</span>
                                            ) : row.vestigios_encontrados ? (
                                                <span className="badge-trace" style={{ whiteSpace: 'nowrap' }}>Vestígios</span>
                                            ) : <span className="text-muted">-</span>}
                                        </td>
                                        <td className="td-center border-left-soft text-small">
                                            {temExame
                                                ? new Date(row.exame_vinculado.data_exame.replace(/-/g, '/')).toLocaleDateString()
                                                : <span className="text-muted">-</span>}
                                        </td>
                                        <td className="td-center text-small" style={{ fontStyle: 'italic', color: '#4b5563', whiteSpace: 'nowrap' }}>
                                            {nomeVetor}
                                        </td>
                                        <td className="td-center">
                                            {temExame ? (
                                                algumPositivo
                                                    ? <span className="badge-positive">POSITIVO</span>
                                                    : <span className="text-muted text-xs">Negativo</span>
                                            ) : '-'}
                                        </td>
                                        <td className="td-center">
                                            <div className="action-cell">
                                                {temExame ? (
                                                    <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                                                        <button className="borr-btn-edit" onClick={() => openExamModal(row, 'edit')}>
                                                            Visualizar / Editar
                                                        </button>
                                                        <button className="borr-btn-delete" title="Excluir" onClick={() => handleDeleteExam(row.exame_vinculado.id)}>
                                                            🗑️
                                                        </button>
                                                    </div>
                                                ) : !temEtiqueta ? (
                                                    <button className="btn-primary-solid" onClick={() => alert('Este formulário de campo não possui código de etiqueta. Para cadastrar um exame, cadastre o código de etiqueta no formulário de campo.')}>
                                                        Cadastrar
                                                    </button>
                                                ) : (
                                                    <button className="btn-primary-solid" onClick={() => openExamModal(row, 'create')}>
                                                        Cadastrar
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {dataDisplay.length === 0 && <div className="empty-state">Nenhum registro encontrado.</div>}

                    {serverTotal > 0 && (
                        <div className="pagination-footer">
                            <button className="page-btn" disabled={serverPage === 1} onClick={() => onPageChange(serverPage - 1)}>
                                Anterior
                            </button>
                            <div className="page-numbers">
                                <button className="page-number active">{serverPage}</button>
                            </div>
                            <button className="page-btn" disabled={serverPage >= totalPages} onClick={() => onPageChange(serverPage + 1)}>
                                Próximo
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {examModalState.isOpen && (
                <ExamModal
                    isOpen={examModalState.isOpen}
                    initialEtiqueta={examModalState.etiquetaInicial}
                    ocorrenciaId={examModalState.ocorrenciaId}
                    readOnly={examModalState.mode === 'view'}
                    existingData={examModalState.examData}
                    fieldData={registros.find(r => r.id === examModalState.ocorrenciaId)}
                    municipioNome={municipioNome}
                    onClose={() => setExamModalState(p => ({ ...p, isOpen: false }))}
                    onSave={handleSaveExam}
                />
            )}
        </div>
    );
};

export default ExamesTab;
